<template>
  <div class="package-selection-page">
    <div class="page-header">
      <h1 class="page-title">选择适合您的{{ currentTypeText }}</h1>
      <p class="page-description">
        我们提供多种{{ currentTypeText }}选择<span @click="handleToggleType">，</span>满足不同用户的需求，从个人使用到企业级应用，总有一款适合您
      </p>
    </div>

    <a-empty
      v-if="packageList.length === 0 && !loading"
      :image="simpleImage"
      :description="`暂无可用${currentTypeText}`"
    >
      <div class="empty-actions">
      </div>
    </a-empty>

    <div class="loading-state" v-if="loading">
      <a-spin size="large" tip="正在加载套餐信息..." />
    </div>

    <div class="package-cards" v-if="packageList.length > 0 && !loading">
      <a-row :gutter="24">
        <a-col :xs="24" :sm="8" v-for="packageItem in packageList" :key="packageItem.id">
          <div
            class="package-card"
            :class="{
              'selected-package': selectedPackageId === packageItem.id,
              'purchased-package': isSelectedPackage(packageItem) && !selectedPackageId
            }"
            @click="selectPackage(packageItem)"
          >

            <div class="selected-badge" v-if="selectedPackageId === packageItem.id">已选择</div>
            <div class="purchased-badge" v-if="isSelectedPackage(packageItem)">已购选套餐</div>

            <div class="card-header">
              <h3 class="package-name">{{ packageItem.packageName }}</h3>
              <div class="package-price">
                <span class="price-symbol">¥</span>
                <span class="price-number">{{ packageItem.price }}</span>
                <span class="price-period"> / {{ getPeriodText(packageItem) }}</span>
              </div>
            </div>
            <div class="card-content">
              <p class="package-description">{{ packageItem.description }}</p>
              <div class="feature-list">
                <div class="feature-item">
                  <span>{{ $formatCapacity(packageItem.capacity) }} 存储空间</span>
                </div>
                <div class="feature-item" v-if="packageItem.packageType === 3">
                  <span>单价 ¥{{ packageItem.unitPrice?.toFixed(2) }}/GB</span>
                </div>
                <div class="feature-item">
                  <span>{{ getValidityText(packageItem) }}</span>
                </div>
              </div>
            </div>
            <div class="card-footer">
              <a-button type="primary" class="purchase-button" @click.stop="handlePurchase(packageItem)">立即购买</a-button>
            </div>
          </div>
        </a-col>
      </a-row>
    </div>

    <PaymentModal ref="paymentModal" :packageInfo="selectedPackage" @payment-success="handlePaymentSuccess" />
  </div>
</template>

<script>
import PaymentModal from './modules/PaymentModal.vue'
import { Empty } from 'ant-design-vue'
import { fetchLists } from '@/api/VR/storagePackage'
import events from '@/components/MultiTab/events'
export default {
  name: 'PackageSelection',
  components: {
    PaymentModal,
  },
  data() {
    return {
      packageList: [],
      selectedPackage: null,
      selectedPackageId: null,
      loading: false,
      type: null,
      tenantId: null,
      selectedId: null,
      simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
      hasUserSelected: false,
      currentType: null,
      clickCount: 0,
      clickTimer: null,
      shouldShowFlexiblePackage: false,
      toggleState: 1, // 1 表示类型A，2表示类型B
      lastToggleTime: null,
    }
  },
  computed: {
    currentTypeText() {
      if (this.shouldShowFlexiblePackage) {
        return '灵活套餐'
      }

      if (this.$route.query.id) {
        const routeType = parseInt(this.$route.query.type)
        return routeType === 1 ? '套餐' : '灵活套餐'
      }

      const routeType = parseInt(this.$route.query.type)
      return routeType === 1 ? '套餐' : '扩容包'
    },
  },
  mounted() {
    this.currentType = this.$route.query.type || 1
    this.getPackageList()
  },
  beforeUnmount() {
    if (this.clickTimer) {
      clearTimeout(this.clickTimer)
    }
  },
  methods: {
    async getPackageList(type = null) {
      if (type !== null) {
        this.currentType = type
      }

      const queryType = this.currentType
      const tenantId = this.$route.query.tenantId
      this.tenantId = tenantId || ''
      const packagePid = this.$route.query.packagePid || ''
      this.selectedId = this.$route.query.id || null

      this.loading = true
      try {
        const response = await fetchLists({
          packageType: queryType,
          packagePid: packagePid,
        })
        this.packageList = response.data.records || []

        if (this.selectedId && !this.hasUserSelected) {
          const selectedPackage = this.packageList.find(item => item.id === this.selectedId)
          if (selectedPackage) {
            this.selectedPackageId = selectedPackage.id
            this.selectedPackage = {
              chooseType: 1,
              id: selectedPackage.id,
              name: selectedPackage.packageName,
              price: selectedPackage.price,
              capacity: selectedPackage.capacity,
              description: selectedPackage.description,
              validDays: selectedPackage.validDays,
              packageType: selectedPackage.packageType,
              orderNumber: this.generateOrderNumber(),
              tenantId: this.tenantId || '',
            }
          }
        }
      } catch (error) {
        this.$message.error('获取套餐列表失败')
      } finally {
        this.loading = false
      }
    },

    handleToggleType() {
      console.log(this.currentType, 'this.currentType ')
      // 如果是扩容包页面（type=3），完全禁用切换功能
      if (this.currentType == 3) {
        return
      }
      this.toggleType()
    },
    toggleType() {
      if (this.currentType == 3) {
        return
      }
      const now = Date.now()
      if (this.lastToggleTime && now - this.lastToggleTime > 5000) {
        this.clickCount = 0
      }
      this.lastToggleTime = now

      // 增加点击计数
      this.clickCount++

      // 清除之前的定时器
      if (this.clickTimer) {
        clearTimeout(this.clickTimer)
      }

      // 设置新的定时器，5秒后重置点击计数
      this.clickTimer = setTimeout(() => {
        this.clickCount = 0
      }, 5000)

      // 只有在点击三次时才执行切换操作
      if (this.clickCount === 3) {
        // 重置点击计数
        this.clickCount = 0

        // 交替切换状态：1 -> 2 -> 1 -> 2
        if (this.toggleState === 1) {
          this.toggleState = 2
          // 执行切换到类型B的操作
          if (this.$route.query.id && this.$route.query.from === 'tenant') {
            this.shouldShowFlexiblePackage = true
          } else {
            this.getPackageList(2) // 切换到类型B
          }
        } else {
          this.toggleState = 1
          // 执行切换到类型A的操作
          if (this.$route.query.id && this.$route.query.from === 'tenant') {
            this.shouldShowFlexiblePackage = false
          } else {
            this.getPackageList(1) // 切换到类型A
          }
        }

        // 清除定时器
        if (this.clickTimer) {
          clearTimeout(this.clickTimer)
        }
      }

      // 点击1次或2次时，不执行任何切换操作
    },
    selectPackage(packageItem) {
      this.hasUserSelected = true

      if (this.selectedPackageId === packageItem.id) {
        this.selectedPackageId = null
        this.selectedPackage = null
      } else {
        this.selectedPackageId = packageItem.id
        this.selectedPackage = {
          chooseType: this.selectedId ? 1 : 0,
          id: packageItem.id,
          name: packageItem.packageName,
          price: packageItem.price,
          capacity: packageItem.capacity,
          description: packageItem.description,
          validDays: packageItem.validDays,
          packageType: packageItem.packageType,
          orderNumber: this.generateOrderNumber(),
          tenantId: this.tenantId || '',
        }
      }
    },

    getPeriodText(packageItem) {
      if (packageItem.packageType === 3) {
        return '随主套餐'
      }
      const validDays = packageItem.validDays
      if (validDays === 365) {
        return '年'
      } else if (validDays === 30) {
        return '月'
      }
      return validDays + '天'
    },
    getValidityText(packageItem) {
      if (packageItem.packageType === 3) {
        return '有效期 随主套餐'
      }
      return `有效期 ${packageItem.validDays} 天`
    },
    isSelectedPackage(packageItem) {
      return this.selectedId && packageItem.id === this.selectedId
    },
    handlePurchase(packageItem) {
      this.selectedPackage = {
        chooseType: this.selectedId ? 1 : 0,
        id: packageItem.id,
        name: packageItem.packageName,
        price: packageItem.price,
        capacity: packageItem.capacity,
        description: packageItem.description,
        validDays: packageItem.validDays,
        packageType: packageItem.packageType,
        orderNumber: this.generateOrderNumber(),
        tenantId: this.tenantId || '',
      }
      this.selectedPackageId = packageItem.id
      this.$refs.paymentModal.show(this.selectedPackage)
    },
    generateOrderNumber() {
      return 'ORD' + Date.now() + Math.floor(Math.random() * 1000)
    },
    handlePaymentSuccess() {
      this.$message.success('购买成功！')
      this.$router.go(-1)
      const pages = this.$store.pages
       events.$emit('close', pages.indexOf(this.$route))
    },
    refreshPage() {
      this.getPackageList()
    },
    contactSupport() {
      this.$message.info('请联系客服热线：400-xxx-xxxx')
    },
  },
}
</script>

<style scoped>
.package-selection-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.page-header {
  text-align: center;
  margin-bottom: 48px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 16px;
  line-height: 1.2;
}

.page-description {
  font-size: 16px;
  color: #666666;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

.page-description span:hover {
  color: #40a9ff;
}

.package-cards {
  margin-top: 40px;
}

.package-card {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: 400px;
  position: relative;
  margin-bottom: 20px;
  cursor: pointer;
}

.package-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.popular-package {
  border: 2px solid #4F46E5;
  position: relative;
  overflow: hidden;
}

.selected-package {
  border: 2px solid #4F46E5;
  background: #f0f8ff;
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(79, 70, 229, 0.15);
}

.purchased-package {
  border: 2px solid #fa8c16;
  background: #fff7e6;
}

.popular-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: #4F46E5;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 0 8px 0 8px;
  z-index: 1;
}

.selected-badge {
  position: absolute;
  top: 0;
  left: 0;
  background-color: #52c41a;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 8px 0 8px 0;
  z-index: 1;
}

.purchased-badge {
  position: absolute;
  top: 40px;
  right: 0;
  background-color: #fa8c16;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 8px 0  0 8px;
  z-index: 1;
}

.card-header {
  text-align: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 20px;
}

.package-name {
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
  line-height: 1.4;
}

.package-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
}

.price-symbol {
  font-size: 16px;
  color: #ff4d4f;
  font-weight: 500;
}

.price-number {
  font-size: 32px;
  color: #ff4d4f;
  font-weight: 600;
  line-height: 1;
}

.price-period {
  font-size: 14px;
  color: #666666;
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.package-description {
  font-size: 14px;
  color: #666666;
  line-height: 1.6;
  margin: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 8px 0;
  transition: min-height 0.2s ease;
}

.feature-list {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #262626;
  line-height: 1.5;
}

.card-footer {
  margin-top: 24px;
  text-align: center;
}

.purchase-button {
  width: 100%;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  background: #4F46E5;
  border-color: #4F46E5;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.045);
}

.purchase-button:hover,
.purchase-button:focus {
  background: #4F46E5;
  border-color: #4F46E5;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.3);
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.empty-actions {
  margin-top: 16px;
}

.empty-actions .ant-btn {
  margin: 0 8px;
}

@media (max-width: 768px) {
  .package-selection-page {
    padding: 24px 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-description {
    font-size: 14px;
  }

  .package-cards .ant-col {
    margin-bottom: 24px;
  }

  .purchased-badge {
    top: 35px;
    font-size: 10px;
  }
}
</style>
